import React, {FC, ReactElement, useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import useExecWithToken from '../../utils/FetchUtils';
import {SERVER_URL, TOS_URL, USER_URL, UserInfo} from '../../contexts/Configuration';

const updateTosAccepted = async (token: string, signature: string, tosAccepted: boolean) => {
    const response = await fetch(`${SERVER_URL}${USER_URL}/tos`, {
        method: 'PATCH',
        headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
        body: JSON.stringify({signature, tosAccepted})
    });

    console.log('updateTosAccepted -> ', response)
    if (response.ok) {
        return await response.json() as UserInfo;
    } else {
        throw new Error(`Fail to get User Info`);
    }
}

const fetchTs = async () => {
    const response = await fetch(TOS_URL)

    if (response.ok) {
        return await response.text();
    } else {
        throw new Error(`Failed to get Terms of Services`);
    }

}

const TosDialog: FC<TosDialogProps> = ({onTosAccepted}): ReactElement => {

    const [tosSupportOpen, setTosSupportOpen] = useState<boolean>(false)
    const [acceptTosButtonDisabled, setAcceptTosButtonDisabled] = useState<boolean>(true);
    const [signature, setSignature] = useState<string>('');

    const execWithToken = useExecWithToken();

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSignature(event.target.value);
        if (event.target.value === '') {
            setAcceptTosButtonDisabled(true);
        } else {
            setAcceptTosButtonDisabled(false);
        }
    }

    const handleTosAccepted = (_signature: string) => {
        execWithToken((token) => {
            updateTosAccepted(token, _signature, true)
                .then(userInfo => {
                    onTosAccepted(userInfo.tosAccepted)
                    saveTosAccepted(userInfo.email, true)
                })
        });
    }

    const handleTosNotAccepted = () => {
        setTosSupportOpen(true);
    }

    useEffect(() => {
        fetchTs()
            .then((response) => {
                console.log('tos -> ', response)
                const tosContentDiv = document.getElementById("tosContentDiv");
                if (document != null && tosContentDiv != null) {
                    tosContentDiv.innerHTML = response;
                }
            })
    }, []);

    return (
        <>
            <Dialog maxWidth="md" open={true}>
                <DialogTitle>
                    <Typography className="br-dialog-title" variant="h4">Welcome to Enersound Cloud</Typography>
                    <Typography className="br-dialog-subtitle" variant="subtitle2">
                        To continue, please read and accept Enersound's General Terms of Service
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <div style={{height: '416px', width: '100%'}} id="tosContentDiv"/>
                </DialogContent>
                <DialogActions>
                    <Typography>I,</Typography>
                    <TextField placeholder="enter your name here"
                               variant="outlined"
                               size="small"
                               value={signature}
                               onChange={handleNameChange}/>
                    <Typography>have read and agree to the terms of service</Typography>
                    <Button sx={{marginLeft: '12px'}}
                            variant="contained"
                            disabled={acceptTosButtonDisabled}
                            onClick={() => handleTosAccepted(signature)}>
                        <span>Agree</span>
                    </Button>
                    <Button variant="outlined"
                            onClick={handleTosNotAccepted}>
                        <span>I do not agree</span>
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={tosSupportOpen}>
                <DialogContent>
                    <Alert title="Have any questions?" severity="info">
                        <Typography>Terms and conditions.</Typography>
                        <br></br>
                        <Typography>We understand that agreeing to the terms and conditions is an important decision.
                            Please note that in order to use Enersound Cloud, you must accept our terms and conditions.
                            If you have any questions or concerns, our support team is here to help and address any
                            issues you may have.
                            <br/>
                            Please contact us at <b>1-800-644-5090</b> (toll-free) or <b>1-305-731-2416</b>, or email us
                            at info@enersound.com.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setTosSupportOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default TosDialog;

type TosDialogProps = {
    onTosAccepted: (tosAccepted: boolean) => void
}

export const getTosAccepted = async (token: string, userEmail: string) => {
    let _tosAccepted = false
    const _sessionStorageTosAccepted = sessionStorage.getItem('tosAccepted');
    if (_sessionStorageTosAccepted) {
        const mailAndAcceptance = _sessionStorageTosAccepted.split(':');
        if (mailAndAcceptance[0] !== userEmail) {
            sessionStorage.removeItem('tosAccepted');
        } else {
            _tosAccepted = mailAndAcceptance.length > 1 && mailAndAcceptance[1] === 'true';
        }
    }

    if (!_tosAccepted) {
        const user = await getUserInfo(token);
        _tosAccepted = user.tosAccepted;
        saveTosAccepted(userEmail, _tosAccepted);
    }

    return _tosAccepted;
}

const saveTosAccepted = (userEmail: string, tosAccepted: boolean) => {
    sessionStorage.setItem('tosAccepted', `${userEmail}:${tosAccepted}`);
}

const getUserInfo = async (token: string) => {
    const response = await fetch(`${SERVER_URL}${USER_URL}/info`, {
        method: 'GET',
        headers: {Authorization: `Bearer ${token}`}
    });
    console.log('getUserInfo -> ', response)
    if (response.ok) {
        return await response.json() as UserInfo;
    } else {
        throw new Error(`Fail to get User Info`);
    }
}

